import React, {useMemo} from 'react';
import {Button, Spin, TableColumnConfig, Text} from '@gravity-ui/uikit';
import {DataTable, formatCurrency} from '../../shared';
import {BalanceAdjustmentModel, useUsersByIds} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {formatDate} from 'date-fns';
import styles from './ProjectBalanceAdjustmentsTable.module.scss';

interface ProjectBalanceAdjustmentsTableProps {
  adjustments: BalanceAdjustmentModel[] | undefined;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const ProjectBalanceAdjustmentsTable = ({
  adjustments,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ProjectBalanceAdjustmentsTableProps) => {
  const SKELETON_ROWS = 5;

  const userIds = useMemo(() => {
    if (!adjustments) return [];
    return Array.from(new Set(adjustments.map((item) => item.userId).filter(Boolean)));
  }, [adjustments]);

  const {usersData, isLoading: isUsersLoading} = useUsersByIds(userIds);

  const columns = useMemo<TableColumnConfig<BalanceAdjustmentModel>[]>(
    () => [
      {
        id: 'createdAt',
        name: 'Дата создания',
        template: (item: BalanceAdjustmentModel) => (
          <Text variant="body-1">{formatDate(new Date(item.createdAt), 'dd.MM.yyyy HH:mm')}</Text>
        ),
      },
      {
        id: 'userId',
        name: 'Кто внес',
        template: (item: BalanceAdjustmentModel) => (
          <Text variant="body-1">{usersData[item.userId]?.displayName || 'Загрузка...'}</Text>
        ),
      },
      {
        id: 'amount',
        name: 'Сумма',
        template: (item: BalanceAdjustmentModel) => <Text variant="body-1">{formatCurrency(item.amount)}</Text>,
      },
      {
        id: 'reason',
        name: 'Комментарий',
        template: (item: BalanceAdjustmentModel) => <Text variant="body-1">{item.reason}</Text>,
      },
    ],
    [usersData],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    fetchNextPage,
  });

  // Показываем скелетон, пока загружаются операции или пользователи
  const isTableLoading = (isLoading || isUsersLoading) && !isFetchingNextPage;

  return (
    <div className={styles.tableContainer}>
      <DataTable<BalanceAdjustmentModel>
        isLoading={isTableLoading}
        data={adjustments || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Операции не найдены"
        emptyStateDescription="Операции внесения не найдены"
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
